import { tokensRaw } from '@vp/swan'
import { MINIMUM_TILE_DEFAULT_WIDTH, MINIMUM_TILE_LARGE_WIDTH, TILE_PREVIEW_SIZE } from 'client/constants'
import { PREVIEW_TYPE, SCENE_TYPE } from 'shared/constants'
import { PersonalizedUrlBuilder } from '~/client/utils/tilePersonalization/personalizedUrlBuilder'
import { addQueryParamToUrl } from '~/shared/url'

type RenderingURLQueryParams = {
  url: string
  locale: string
  templateToken: string
  previewWidth: number
  sceneUri?: string
  imageCacheVersion: string | null
  sceneType?: Gallery.ContentQuery.SCENE_TYPE
  isPersonalizationSidebarEnabled?: boolean
  designPersonalizationContextId?: string
  productOptions: Record<string, string>
  productVersion: number
  productId: string
  viewId?: string
  isDynamicDesign: boolean
  smartCropEnabled?: boolean
}

export const DEFAULT_BACKGROUND_COLOR = tokensRaw.SwanSemColorBgStrong.substring(1)

const DEFAULT_DYNAMIC_PARAMS: Record<string, string> = {
  format: 'auto',
  quality: '95',
  showerr: 'true',
  category: 'gal6',
}

const addRenderingURLQueryParams = ({
  url,
  locale,
  templateToken,
  previewWidth,
  sceneUri,
  imageCacheVersion = null,
  sceneType,
  isPersonalizationSidebarEnabled = false,
  designPersonalizationContextId,
  productOptions,
  productVersion,
  productId,
  viewId,
  isDynamicDesign,
  smartCropEnabled = false,
}: RenderingURLQueryParams): string => {
  const updatedURL = new URL(url.startsWith('//') ? `https:${url}` : url)
  const isVortexScene = sceneType === SCENE_TYPE.VORTEX_RASTER
  const isDynamicScene = sceneType === SCENE_TYPE.DYNAMIC

  const instructionsUriParam = isVortexScene ? 'renderingInstructionsUrl' : 'instructions_uri'

  const instructionsUrlString = updatedURL.searchParams.get(instructionsUriParam)

  if (!instructionsUrlString) {
    throw new Error('No instructions URI')
  }
  const instructionsUrl = new URL(instructionsUrlString)

  if (isDynamicScene) {
    // Ignore projections for dynamic scenes
    instructionsUrl.searchParams.set('ignoreProjection', 'true')
  }

  if (isPersonalizationSidebarEnabled && designPersonalizationContextId) {
    if (isDynamicDesign) {
      const diamondUri = PersonalizedUrlBuilder.buildDiamondDynamicUri(instructionsUrlString, designPersonalizationContextId)

      instructionsUrl.searchParams.set('documentUri', diamondUri)
    } else {
      const diamondUri = PersonalizedUrlBuilder.buildDiamondUri(
        templateToken,
        locale,
        productId,
        productOptions,
        productVersion,
        designPersonalizationContextId,
        viewId,
        smartCropEnabled,
      )

      instructionsUrl.searchParams.set('documentUri', diamondUri)
    }
  }

  const newSceneUri = sceneUri && imageCacheVersion ? addQueryParamToUrl(sceneUri, 'icv', imageCacheVersion) : sceneUri

  const newParams: Record<string, string | undefined> = isVortexScene
    ? {
        bgColor: DEFAULT_BACKGROUND_COLOR,
      }
    : {
        ...DEFAULT_DYNAMIC_PARAMS,
        merchant_metadata: templateToken,
        scene: newSceneUri,
        bgcolor: DEFAULT_BACKGROUND_COLOR,
      }

  newParams[instructionsUriParam] = instructionsUrl.toString()
  newParams.width = Number(previewWidth).toString()

  for (const [key, value] of Object.entries(newParams)) {
    if (value !== undefined) {
      updatedURL.searchParams.set(key, value)
    }
  }

  return updatedURL.toString()
}

export const getPreviewData = (
  previewInfo: Gallery.ContentQuery.PreviewInfo,
  locale: i18n.Locale,
  previewType: PREVIEW_TYPE,
  previewUrls: Gallery.ContentQuery.PreviewUrls,
  isPersonalizationSidebarEnabled: boolean,
  isDynamicDesign: boolean,
  previewSize: Gallery.Models.Url.ValidParsedQsValue<string> = TILE_PREVIEW_SIZE.DEFAULT,
  imageCacheVersion: Gallery.Models.Url.ValidParsedQsValue<string> = null,
  productKey = '',
  productVersion = 0,
  fullProductOptions: Record<string, string> = {},
  designPersonalizationContextId: string | undefined = undefined,
  viewId: string | undefined = undefined,
  smartCropEnabled = false,
): Gallery.Designs.RenderablePreviewInfo => {
  let previewWidth = (previewSize && previewSize === TILE_PREVIEW_SIZE.LARGE ? MINIMUM_TILE_LARGE_WIDTH : MINIMUM_TILE_DEFAULT_WIDTH) * 2

  if (previewType === PREVIEW_TYPE.QUICKVIEW) {
    previewWidth *= 3
  }

  const sceneUri = (previewInfo.scenes && previewInfo.scenes[PREVIEW_TYPE.TILE]?.uri) || undefined
  const sceneType = previewInfo.scenes && previewInfo.scenes[PREVIEW_TYPE.TILE]?.sceneType
  const commonPreviewURLParams = {
    locale,
    templateToken: previewInfo.templateToken,
    previewWidth,
    imageCacheVersion,
    sceneUri,
    sceneType,
    isPersonalizationSidebarEnabled,
    productVersion,
    productOptions: fullProductOptions,
    productId: productKey,
    designPersonalizationContextId,
    viewId,
    isDynamicDesign,
    smartCropEnabled,
  }

  const builtPreviewUrls = {
    size1x: addRenderingURLQueryParams({
      ...commonPreviewURLParams,
      url: previewUrls?.size1x,
    }),
    size2x: addRenderingURLQueryParams({
      ...commonPreviewURLParams,
      url: previewUrls?.size2x,
    }),
  }

  let personalizedInstructionsUrl

  if (isPersonalizationSidebarEnabled && designPersonalizationContextId) {
    if (isDynamicDesign && previewInfo.instructionsUrl) {
      personalizedInstructionsUrl = PersonalizedUrlBuilder.buildDiamondDynamicInstructionsUri(previewInfo.instructionsUrl, designPersonalizationContextId)
    } else {
      personalizedInstructionsUrl = PersonalizedUrlBuilder.buildDiamondInstructionsUri(
        previewInfo.templateToken,
        locale,
        productKey,
        fullProductOptions,
        productVersion,
        designPersonalizationContextId,
        viewId,
        smartCropEnabled,
      )
    }
  }

  return {
    previewInfo,
    previewWidth,
    personalizedInstructionsUrl,
    previewsUrls: builtPreviewUrls,
  }
}
